import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, BadgeCheck, CheckCircle2, Clock, Star } from "lucide-react";
import { buildRequestUrl, contextForSlug, trackHome } from "@/lib/requestContext";
import { SectionHeader } from "@/components/marketing/ContentSections";
import { DynIcon } from "@/components/marketing/RequestSections";

const cx = (...c) => c.filter(Boolean).join(" ");
const TONES = { white: "bg-white", light: "bg-slate-50", sand: "bg-[#f5f0e8]", dark: "bg-[#071b22] text-white" };

function useStart() {
  const navigate = useNavigate();
  return async (slug, source, component, extra = {}) => {
    trackHome(slug ? "category_select" : "request_click", { source, category: slug });
    const ctx = slug ? await contextForSlug(slug) : {};
    navigate(buildRequestUrl({ ...ctx, request_source: source, homepage_component: component, ...extra }));
  };
}

// ---------------------------------------------------------------- hero extras

export function QuickChips({ chips = [], source = "homepage_hero", mode }) {
  const start = useStart();
  if (!chips.length) return null;
  return (
    <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="hero-quick-chips">
      <span className="text-xs font-semibold uppercase tracking-[0.18em] text-white/45 mr-1">Popular</span>
      {chips.map((c) => (
        <button key={c.category_slug} type="button" onClick={() => mode !== "edit" && start(c.category_slug, source, "hero_chip")}
          className="rounded-full border border-white/15 bg-white/[0.06] px-3.5 py-1.5 text-sm text-white/85 backdrop-blur hover:bg-white/15 hover:text-white transition-colors"
          data-testid={`hero-chip-${c.category_slug}`}>{c.label}</button>
      ))}
    </div>
  );
}

function EstimatePreview({ preview, id = "hero-estimate" }) {
  const offers = preview?.offers || [];
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!offers.length) return;
    const t = setInterval(() => setTick((n) => n + 1), 1600);
    return () => clearInterval(t);
  }, [offers.length]);
  const shown = Math.min((tick % (offers.length + 2)) + 1, offers.length);
  return (
    <div className="w-[min(340px,86vw)] rounded-3xl bg-white/95 p-4 text-[#071b22] shadow-[0_40px_80px_-30px_rgba(0,0,0,0.6)] ring-1 ring-black/5 backdrop-blur-xl" data-testid={`${id}-preview`}>
      <div className="flex items-center justify-between gap-2">
        <span className="rounded-full bg-[#165a71]/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.16em] text-[#165a71]">{preview?.label || "Sample request"}</span>
        <span className="inline-flex items-center gap-1 text-[11px] text-slate-500"><Clock className="h-3 w-3" />{preview?.time || "Sent just now"}</span>
      </div>
      <div className="mt-2.5 font-display text-base font-semibold leading-snug">{preview?.title}</div>
      {preview?.location && <div className="text-xs text-slate-500">{preview.location}</div>}
      <div className="mt-3 h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <motion.div className="h-full rounded-full bg-gradient-to-r from-[#165a71] to-[#5fd3c6]" animate={{ width: `${(Math.min(shown, offers.length) / Math.max(offers.length, 1)) * 100}%` }} transition={{ duration: 0.5 }} />
      </div>
      <div className="mt-1.5 text-[11px] font-medium text-slate-500" data-testid={`${id}-count`}>{`${Math.min(shown, offers.length)} of ${offers.length} estimates in`}</div>
      <div className="mt-3 space-y-2 min-h-[188px]">
        <AnimatePresence>
          {offers.slice(0, shown).map((o) => (
            <motion.div key={o.name} initial={{ opacity: 0, y: 10, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }}
              className="flex items-center gap-3 rounded-2xl border border-slate-100 bg-white p-2.5">
              <div className="h-9 w-9 shrink-0 rounded-xl bg-[#071b22] text-white grid place-items-center text-xs font-bold">{o.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}</div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1 text-sm font-semibold truncate">{o.name}<BadgeCheck className="h-3.5 w-3.5 shrink-0 text-[#165a71]" /></div>
                <div className="text-[11px] text-slate-500 truncate">{o.note}</div>
              </div>
              {o.rating && <div className="inline-flex items-center gap-0.5 text-xs font-semibold"><Star className="h-3 w-3 fill-amber-400 text-amber-400" />{o.rating}</div>}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
}

export function HeroCollage({ images = [], preview }) {
  const [main, a, b] = images;
  return (
    <div className="relative hidden lg:block h-[600px]" data-testid="hero-collage">
      {main && <motion.img initial={{ opacity: 0, scale: 1.04 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9 }} src={main} alt="" className="absolute right-0 top-0 h-[520px] w-[70%] rounded-[2.25rem] object-cover shadow-2xl" />}
      {a && <motion.img initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25, duration: 0.7 }} src={a} alt="" className="absolute left-0 top-6 h-44 w-[36%] rounded-3xl object-cover ring-4 ring-[#071b22] shadow-xl" />}
      {b && <motion.img initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.7 }} src={b} alt="" className="absolute -right-4 bottom-0 h-44 w-[34%] rounded-3xl object-cover ring-4 ring-[#071b22] shadow-xl" />}
      {preview && <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.6, duration: 0.7 }} className="absolute left-0 bottom-4 z-10"><EstimatePreview preview={preview} /></motion.div>}
    </div>
  );
}

export function MobileHeroPhoto({ image, preview }) {
  if (!image) return null;
  return (
    <div className="lg:hidden relative mt-10" data-testid="hero-mobile-photo">
      <img src={image} alt="" className="h-72 w-full rounded-3xl object-cover" />
      {preview && <div className="-mt-24 relative px-3 flex justify-center"><EstimatePreview preview={preview} id="hero-mobile-estimate" /></div>}
    </div>
  );
}

// ---------------------------------------------------------------- photo service card

export function PhotoCard({ item, img, big, dark, onClick, testid, i }) {
  const [bad, setBad] = useState(false);
  return (
    <motion.button type="button" onClick={onClick} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: Math.min(i, 8) * 0.05 }}
      className={cx("group relative snap-start shrink-0 overflow-hidden rounded-[1.75rem] text-left bg-[#0d3644] w-[68vw] sm:w-[40vw] md:w-auto",
        big ? "md:col-span-2 md:row-span-2 min-h-[320px] md:min-h-0" : "min-h-[260px] md:min-h-[240px]",
        "transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_30px_60px_-28px_rgba(7,27,34,0.6)]", dark && "ring-1 ring-white/10")}
      data-testid={testid}>
      {img && !bad ? <img src={img} alt="" loading="lazy" onError={() => setBad(true)} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.06]" />
        : <div className="absolute inset-0 grid place-items-center bg-gradient-to-br from-[#165a71] to-[#071b22]"><DynIcon name={item.icon} className="h-14 w-14 text-white/30" /></div>}
      <div className="absolute inset-0 bg-gradient-to-t from-[#071b22]/90 via-[#071b22]/25 to-transparent" />
      <div className="absolute left-4 top-4 h-10 w-10 rounded-2xl bg-white/15 backdrop-blur-md grid place-items-center text-white ring-1 ring-white/20"><DynIcon name={item.icon} className="h-[18px] w-[18px]" /></div>
      <div className="absolute inset-x-0 bottom-0 p-5">
        <div className={cx("font-display font-semibold text-white leading-tight", big ? "text-2xl md:text-3xl" : "text-lg")}>{item.label}</div>
        {item.description && <div className={cx("mt-1 text-white/70", big ? "text-sm md:text-base max-w-sm" : "text-xs md:text-sm")}>{item.description}</div>}
        <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-white px-3.5 py-1.5 text-xs font-semibold text-[#071b22] opacity-95 group-hover:bg-[#5fd3c6] transition-colors">
          {item.urgency ? "Get help now" : "Get estimates"} <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
        </div>
      </div>
    </motion.button>
  );
}

// ---------------------------------------------------------------- visual how-it-works

export function RequestSteps({ props = {}, mode }) {
  const { eyebrow, headline, lede, steps = [], tone = "white", cta } = props;
  const [active, setActive] = useState(0);
  const start = useStart();
  useEffect(() => {
    if (!steps.length || mode === "edit") return;
    const t = setInterval(() => setActive((a) => (a + 1) % steps.length), 5000);
    return () => clearInterval(t);
  }, [steps.length, mode]);
  const cur = steps[active] || {};
  const dark = tone === "dark";
  return (
    <section className={cx(TONES[tone] || TONES.white, "relative py-20 md:py-28 overflow-hidden")} data-testid="request-steps">
      <div className="mx-auto max-w-7xl px-5 lg:px-10">
        <SectionHeader eyebrow={eyebrow} headline={headline} lede={lede} dark={dark} />
        <div className="mt-12 grid gap-10 lg:grid-cols-[1fr_1.15fr] items-center">
          <ol className="space-y-3">
            {steps.map((s, i) => (
              <li key={i}>
                <button type="button" onClick={() => setActive(i)} onMouseEnter={() => setActive(i)}
                  className={cx("w-full text-left rounded-3xl p-5 md:p-6 transition-[background-color,box-shadow] duration-300 flex gap-5",
                    i === active ? (dark ? "bg-white/[0.08] ring-1 ring-white/15" : "bg-white ring-1 ring-slate-200 shadow-[0_24px_48px_-28px_rgba(7,27,34,0.35)]") : (dark ? "hover:bg-white/[0.04]" : "hover:bg-slate-50"))}
                  data-testid={`request-step-${i}`}>
                  <span className={cx("font-display text-3xl font-semibold tabular-nums transition-colors", i === active ? "text-[#165a71]" : dark ? "text-white/25" : "text-slate-300")}>{String(i + 1).padStart(2, "0")}</span>
                  <span className="flex-1">
                    <span className={cx("block font-display text-lg md:text-xl font-semibold", dark ? "text-white" : "text-[#071b22]")}>{s.title}</span>
                    <span className={cx("mt-1 block text-sm md:text-base leading-relaxed", dark ? "text-white/65" : "text-slate-500")}>{s.body}</span>
                    {i === active && mode !== "edit" && <span className="mt-4 block h-0.5 rounded-full bg-slate-100 overflow-hidden"><motion.span key={active} className="block h-full bg-[#165a71]" initial={{ width: 0 }} animate={{ width: "100%" }} transition={{ duration: 5, ease: "linear" }} /></span>}
                    {s.image && <img src={s.image} alt="" loading="lazy" className="lg:hidden mt-4 h-48 w-full rounded-2xl object-cover" />}
                  </span>
                </button>
              </li>
            ))}
          </ol>
          <div className="relative hidden lg:block h-[520px]">
            <AnimatePresence mode="wait">
              <motion.img key={active} src={cur.image} alt="" initial={{ opacity: 0, scale: 1.03 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}
                className="absolute inset-0 h-full w-full rounded-[2.25rem] object-cover shadow-2xl" />
            </AnimatePresence>
            {cur.chip && (
              <motion.div key={`chip-${active}`} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
                className="absolute left-6 bottom-6 flex items-center gap-3 rounded-2xl bg-white/95 px-4 py-3 shadow-xl backdrop-blur-xl" data-testid="request-step-chip">
                <span className="h-9 w-9 rounded-xl bg-[#165a71]/10 text-[#165a71] grid place-items-center"><DynIcon name={cur.icon || "check"} className="h-4 w-4" /></span>
                <span><span className="block text-sm font-semibold text-[#071b22]">{cur.chip}</span>{cur.chip_sub && <span className="block text-xs text-slate-500">{cur.chip_sub}</span>}</span>
              </motion.div>
            )}
          </div>
        </div>
        {cta?.label && (
          <button type="button" onClick={() => mode !== "edit" && start(null, "how_it_works", "how_it_works")} className="mt-12 inline-flex items-center gap-2 rounded-full bg-[#165a71] px-7 py-3.5 text-sm font-semibold text-white shadow-lg shadow-[#165a71]/25 hover:bg-[#0f4657] active:scale-[0.98] transition-[background-color,transform]" data-testid="request-steps-cta">
            {cta.label} <ArrowRight className="h-4 w-4" />
          </button>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------- trust strip + guarantee

export function TrustStrip({ props = {} }) {
  const { items = [], tone = "sand" } = props;
  const dark = tone === "dark";
  return (
    <section className={cx(TONES[tone] || TONES.sand, "relative border-y", dark ? "border-white/10" : "border-[#e8dfd0]")} data-testid="trust-strip">
      <div className="mx-auto max-w-7xl px-5 lg:px-10 py-8 md:py-10 grid grid-cols-2 gap-6 md:grid-cols-3 lg:grid-cols-6">
        {items.map((it, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }} className="flex items-start gap-3" data-testid={`trust-item-${i}`}>
            <span className={cx("h-10 w-10 shrink-0 rounded-2xl grid place-items-center", dark ? "bg-white/10 text-[#87bccc]" : "bg-white text-[#165a71] shadow-sm")}><DynIcon name={it.icon} className="h-[18px] w-[18px]" /></span>
            <span>
              <span className={cx("block text-sm font-semibold leading-tight", dark ? "text-white" : "text-[#071b22]")}>{it.title}</span>
              <span className={cx("mt-0.5 block text-xs leading-snug", dark ? "text-white/55" : "text-slate-500")}>{it.body}</span>
            </span>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

export function GuaranteePanel({ props = {}, mode }) {
  const { eyebrow, headline, lede, image, points = [], badge, cta, secondary_cta } = props;
  const start = useStart();
  return (
    <section className="relative bg-[#071b22] py-20 md:py-28 text-white overflow-hidden" data-testid="guarantee-panel">
      <div className="absolute inset-0 gcw-noise opacity-25" aria-hidden />
      <div className="absolute -right-40 -top-40 h-[28rem] w-[28rem] rounded-full bg-[#165a71]/40 blur-3xl" aria-hidden />
      <div className="relative mx-auto max-w-7xl px-5 lg:px-10 grid gap-12 lg:grid-cols-2 items-center">
        <div className="relative order-2 lg:order-1">
          {image && <img src={image} alt="" loading="lazy" className="h-[340px] md:h-[480px] w-full rounded-[2.25rem] object-cover" />}
          {badge && (
            <div className="absolute -bottom-6 right-6 md:right-10 rounded-3xl bg-white p-5 text-[#071b22] shadow-2xl max-w-[260px]" data-testid="guarantee-badge">
              <div className="flex items-center gap-2 text-[#165a71]"><DynIcon name={badge.icon || "shield-check"} className="h-5 w-5" /><span className="text-xs font-bold uppercase tracking-[0.16em]">{badge.eyebrow}</span></div>
              <div className="mt-2 font-display text-lg font-semibold leading-snug">{badge.title}</div>
              {badge.body && <div className="mt-1 text-xs text-slate-500">{badge.body}</div>}
            </div>
          )}
        </div>
        <div className="order-1 lg:order-2">
          <SectionHeader eyebrow={eyebrow} headline={headline} lede={lede} dark />
          <ul className="mt-8 space-y-4">
            {points.map((p, i) => (
              <motion.li key={i} initial={{ opacity: 0, x: 12 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.07 }} className="flex gap-3" data-testid={`guarantee-point-${i}`}>
                <CheckCircle2 className="h-5 w-5 mt-0.5 shrink-0 text-[#5fd3c6]" />
                <span><span className="font-semibold">{p.title}</span>{p.body && <span className="text-white/60"> — {p.body}</span>}</span>
              </motion.li>
            ))}
          </ul>
          <div className="mt-10 flex flex-wrap gap-3">
            {cta?.label && <button type="button" onClick={() => mode !== "edit" && start(null, "guarantee", "guarantee")} className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3.5 text-sm font-semibold text-[#071b22] hover:bg-[#5fd3c6] transition-colors" data-testid="guarantee-cta">{cta.label} <ArrowRight className="h-4 w-4" /></button>}
            {secondary_cta?.label && (mode === "edit" ? <span className="px-6 py-3.5 text-sm">{secondary_cta.label}</span>
              : <Link to={secondary_cta.href || "/trust"} className="inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3.5 text-sm font-semibold text-white hover:bg-white/10 transition-colors" data-testid="guarantee-secondary">{secondary_cta.label}</Link>)}
          </div>
        </div>
      </div>
    </section>
  );
}
